import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adoptSignInTenant, tokenStorage } from '@/api/tokenManager';
import { useAuth } from '@/contexts/shared/AuthContext';
import { PageLoader } from '@/components/shared/PageLoader/PageLoader';
import { redeemSsoCode } from '@/auth/ssoHandoff';

// The code is single-use: make sure a re-mount (StrictMode) doesn't spend it twice.
let redeeming: Promise<{ access_token: string; refresh_token: string }> | null = null;

/**
 * OAuthCallbackPage
 *
 * The backend redirects here after a successful OAuth / SAML sign-in with a
 * one-time code in the URL fragment: /auth/callback#code=...
 * (src/auth/ssoHandoff.ts). The code is traded for the tokens, then we go to:
 *   1. The originally-intended URL (stored in sessionStorage by useLoginPage before the redirect), or
 *   2. The user dashboard as a fallback.
 */
const OAuthCallbackPage = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  useEffect(() => {
    const code = new URLSearchParams(window.location.hash.slice(1)).get('code');
    // Drop the code from the address bar / history right away.
    window.history.replaceState(window.history.state, '', window.location.pathname);

    const fail = () => {
      redeeming = null;
      navigate('/login?error=sso_failed', { replace: true });
    };
    if (!code && !redeeming) { fail(); return; }

    redeeming = redeeming || redeemSsoCode(code as string);
    redeeming
      .then(({ access_token, refresh_token }) => {
        tokenStorage.setTokens(access_token, refresh_token ?? '');
        adoptSignInTenant(access_token);
        const intendedPath = sessionStorage.getItem('oauth_redirect_after_login') || '/user/dashboard';
        sessionStorage.removeItem('oauth_redirect_after_login');
        return checkAuth().then(() => {
          redeeming = null;
          navigate(intendedPath, { replace: true });
        });
      })
      .catch(fail);
  }, []);

  return <PageLoader fullScreen />;
};

export default OAuthCallbackPage;
